import React, { useState, useEffect, useRef } from 'react';
import { X, Delete } from 'lucide-react';
import { Button } from './ui/button';

const NUMPAD_STORAGE_KEY = 'vg_wallet_use_virtual_numpad';

// Hook to manage virtual numpad preference
export const useVirtualNumpad = () => {
    const [isVirtualNumpadEnabled, setIsVirtualNumpadEnabled] = useState(() => {
        const stored = localStorage.getItem(NUMPAD_STORAGE_KEY);
        return stored === 'true';
    });

    const toggleVirtualNumpad = () => {
        const newValue = !isVirtualNumpadEnabled;
        setIsVirtualNumpadEnabled(newValue);
        localStorage.setItem(NUMPAD_STORAGE_KEY, newValue.toString());
    };

    return { useVirtualNumpad: isVirtualNumpadEnabled, toggleVirtualNumpad };
};

// Virtual Numpad Toggle Switch
export const VirtualNumpadToggle = ({ enabled, onToggle }) => {
    return (
        <button
            type="button"
            onClick={onToggle}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                enabled 
                    ? 'bg-neon-green/20 text-neon-green border border-neon-green/30' 
                    : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600'
            }`}
            data-testid="virtual-numpad-toggle"
        >
            <span className="text-base">⌨️</span>
            {enabled ? 'Numpad: ON' : 'Numpad: OFF'}
        </button>
    );
};

// Virtual Numpad Component
const VirtualNumpad = ({ 
    value, 
    onChange, 
    onClose,
    label = '',
    position = 'bottom' // 'bottom' | 'inline'
}) => {
    const numpadRef = useRef(null);

    // Handle clicking outside to close (only for positioned numpad)
    useEffect(() => {
        if (position !== 'bottom') return;
        
        const handleClickOutside = (e) => {
            if (numpadRef.current && !numpadRef.current.contains(e.target)) {
                onClose?.();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose, position]);

    const handleKeyPress = (key) => {
        let newValue = value || '';
        
        if (key === 'C') {
            // Clear
            newValue = '';
        } else if (key === '⌫') {
            // Backspace
            newValue = newValue.slice(0, -1);
        } else if (key === '±') {
            // Toggle sign
            if (newValue.startsWith('-')) {
                newValue = newValue.substring(1);
            } else if (newValue === '') {
                newValue = '-';
            } else {
                newValue = '-' + newValue;
            }
        } else if (key === '.') {
            // Decimal point - only allow one
            if (!newValue.includes('.')) {
                if (newValue === '' || newValue === '-') {
                    newValue = newValue + '0.';
                } else {
                    newValue = newValue + '.';
                }
            }
        } else {
            // Number
            // Validate: max 1 decimal place
            if (newValue.includes('.')) {
                const parts = newValue.split('.');
                if (parts[1] && parts[1].length >= 1) {
                    // Already has 1 decimal place, don't add more
                    return;
                }
            }
            newValue = newValue + key;
        }
        
        onChange(newValue);
    };

    const keys = [
        ['1', '2', '3'],
        ['4', '5', '6'],
        ['7', '8', '9'],
        ['.', '0', '⌫'],
        ['±', 'C', '✓']
    ];

    return (
        <div 
            ref={numpadRef}
            className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 shadow-xl z-50"
            data-testid="virtual-numpad"
        >
            {/* Display */}
            <div className="mb-3">
                {label && (
                    <p className="text-xs text-zinc-500 mb-1 truncate">{label}</p>
                )}
                <div className="bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-right">
                    <span className={`text-xl font-mono ${
                        value && parseFloat(value) < 0 ? 'text-red-400' : 
                        value && parseFloat(value) > 0 ? 'text-neon-green' : 'text-white'
                    }`}>
                        {value || '0'}
                    </span>
                </div>
            </div>

            {/* Numpad Grid */}
            <div className="grid grid-cols-3 gap-1.5">
                {keys.map((row, rowIdx) => (
                    row.map((key, keyIdx) => {
                        const isAction = ['±', 'C', '⌫', '✓'].includes(key);
                        const isConfirm = key === '✓';
                        
                        return (
                            <Button
                                key={`${rowIdx}-${keyIdx}`}
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    if (isConfirm) {
                                        onClose?.();
                                    } else {
                                        handleKeyPress(key);
                                    }
                                }}
                                className={`h-11 text-lg font-medium ${
                                    isConfirm 
                                        ? 'bg-neon-green text-void-black hover:bg-neon-green/90 border-neon-green' 
                                        : isAction 
                                            ? key === '±' 
                                                ? 'bg-orange-500/20 border-orange-500/30 text-orange-400 hover:bg-orange-500/30'
                                                : key === 'C'
                                                    ? 'bg-red-500/20 border-red-500/30 text-red-400 hover:bg-red-500/30'
                                                    : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700'
                                            : 'bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700'
                                }`}
                                data-testid={`numpad-key-${key === '±' ? 'plusminus' : key === '⌫' ? 'backspace' : key === '✓' ? 'confirm' : key}`}
                            >
                                {key === '⌫' ? <Delete className="w-5 h-5" /> : key}
                            </Button>
                        );
                    })
                ))}
            </div>
        </div>
    );
};

// Score Input with Virtual Numpad support
export const ScoreInputWithNumpad = ({
    userId,
    username,
    value,
    onChange,
    useVirtualNumpad: useNumpad,
    onToggleMinus
}) => {
    const [showNumpad, setShowNumpad] = useState(false);
    const inputRef = useRef(null);
    const containerRef = useRef(null);

    // Handle value change - supports both comma and period as decimal separator
    const handleChange = (newValue) => {
        // Replace comma with period for decimal
        const normalizedValue = newValue.replace(',', '.');
        
        // Validate: allow empty, minus, or valid number format (max 1 decimal)
        if (normalizedValue === '' || normalizedValue === '-' || /^-?\d*\.?\d?$/.test(normalizedValue)) {
            onChange(userId, normalizedValue);
        }
    };

    // Handle native input change
    const handleNativeChange = (e) => {
        const rawValue = e.target.value;
        handleChange(rawValue);
    };

    return (
        <div ref={containerRef} className="flex items-center gap-1.5 relative">
            {/* Username - fixed width, won't shrink */}
            <span className="w-20 sm:w-24 text-white text-xs sm:text-sm truncate flex-shrink-0">{username}</span>
            
            {/* +/- Toggle Button */}
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onToggleMinus(userId)}
                className={`w-8 h-8 sm:w-10 sm:h-9 px-0 font-bold text-base sm:text-lg flex-shrink-0 ${
                    (value || '').startsWith('-') 
                        ? 'bg-red-500/20 border-red-500 text-red-400' 
                        : 'bg-zinc-800 border-zinc-600 text-zinc-400'
                }`}
                title="Toggle +/-"
                data-testid={`toggle-minus-${userId}`}
            >
                ±
            </Button>

            {/* Input Field - font-size 16px to prevent iOS zoom */}
            <div className="relative flex-1 min-w-0">
                <input
                    ref={inputRef}
                    type="text"
                    inputMode={useNumpad ? 'none' : 'decimal'}
                    readOnly={useNumpad}
                    value={value || ''}
                    onChange={handleNativeChange}
                    onClick={() => useNumpad && setShowNumpad(true)}
                    onFocus={() => useNumpad && setShowNumpad(true)}
                    style={{ fontSize: '16px' }}
                    className={`w-full bg-zinc-800 border border-zinc-700 rounded-md px-2 py-1.5 text-center text-white 
                        ${useNumpad ? 'cursor-pointer' : ''} 
                        focus:outline-none focus:ring-2 focus:ring-neon-green/50 focus:border-neon-green`}
                    placeholder=""
                    data-testid={`score-input-${userId}`}
                />

                {/* Virtual Numpad Popup */}
                {useNumpad && showNumpad && (
                    <div className="absolute top-full left-0 mt-2 z-50">
                        <VirtualNumpad
                            value={value || ''}
                            onChange={(newVal) => handleChange(newVal)}
                            onClose={() => setShowNumpad(false)}
                            label={username}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default VirtualNumpad;
